import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { User } from '@supabase/supabase-js';

interface LoyaltyContextType {
  isOpen: boolean;
  openLoyalty: () => void;
  closeLoyalty: () => void;
  points: number;
  tier: 'Rookie' | 'Player' | 'Pro' | 'Legend';
  lifetimePoints: number;
  isLoading: boolean;
  user: User | null;
  refreshPoints: () => Promise<void>;
}

const LoyaltyContext = createContext<LoyaltyContextType | undefined>(undefined);

const getTier = (points: number): 'Rookie' | 'Player' | 'Pro' | 'Legend' => {
  if (points >= 1000) return 'Legend';
  if (points >= 500) return 'Pro';
  if (points >= 100) return 'Player';
  return 'Rookie';
};

export const LoyaltyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [points, setPoints] = useState(0);
  const [lifetimePoints, setLifetimePoints] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [user, setUser] = useState<User | null>(null);

  const openLoyalty = useCallback(() => setIsOpen(true), []);
  const closeLoyalty = useCallback(() => setIsOpen(false), []);

  const tier = getTier(points);

  // Fetch loyalty points from database
  const fetchLoyaltyPoints = useCallback(async (userId: string) => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('loyalty_points')
        .select('points, lifetime_points')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) {
        console.error('Error fetching loyalty points:', error);
        return;
      }

      if (data) {
        setPoints(data.points);
        setLifetimePoints(data.lifetime_points);
      } else {
        // Create loyalty record for user if it doesn't exist
        const { error: insertError } = await supabase
          .from('loyalty_points')
          .insert({ user_id: userId, points: 0, lifetime_points: 0 });

        if (insertError) {
          console.error('Error creating loyalty record:', insertError);
        }
        setPoints(0);
        setLifetimePoints(0);
      }
    } catch (err) {
      console.error('Error in fetchLoyaltyPoints:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Refresh points from database
  const refreshPoints = useCallback(async () => {
    if (user) {
      await fetchLoyaltyPoints(user.id);
    }
  }, [user, fetchLoyaltyPoints]);

  // Listen for auth state changes
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setUser(session?.user ?? null);
        
        // Fetch points after auth state change (deferred to avoid deadlock)
        if (session?.user) {
          setTimeout(() => {
            fetchLoyaltyPoints(session.user.id);
          }, 0);
        } else {
          setPoints(0);
          setLifetimePoints(0);
        }
      }
    );

    // Check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchLoyaltyPoints(session.user.id);
      }
    });

    return () => subscription.unsubscribe();
  }, [fetchLoyaltyPoints]);

  return (
    <LoyaltyContext.Provider value={{
      isOpen,
      openLoyalty,
      closeLoyalty,
      points,
      tier,
      lifetimePoints,
      isLoading,
      user,
      refreshPoints,
    }}>
      {children}
    </LoyaltyContext.Provider>
  );
};

export const useLoyalty = () => {
  const context = useContext(LoyaltyContext);
  if (!context) throw new Error('useLoyalty must be used within LoyaltyProvider');
  return context;
};